import { PayloadAction } from '@reduxjs/toolkit';
import type { CloudAPI } from '@yandex-360/telemost-sdk/cloud-api';
import { Device } from '@yandex-video-platform/goloom-sdk';
import isUndefined from 'lodash/isUndefined';
import { eventChannel } from 'redux-saga';
import { Effect } from 'redux-saga/effects';
import { all, call, delay, fork, put, race, select, take, takeEvery, takeLatest } from 'typed-redux-saga';

import { NoiseCancellationType, UserSettings, VirtualBackgroundType } from '../../../../../common/types/settings';
import { RootState } from '../../../../init-store/index';
import { GRID_ITEM_RATIO, GRID_VIEW } from '../../../pages/call/constants';
import { setRoomConferenceConfig } from '../../../pages/call/features/conference/slice';
import {
    setStoreActiveGridView,
    setStoreGridItemRatio,
    setStoreUserPreferredGridView,
    setUserPreferredGridViewAction,
} from '../../../pages/call/features/goloom-video-grid';
import { isNonProductionEnvironment, selectIsNewGrid } from '../../../pages/call/selectors';
import { ConferenceConfig, NotificationId } from '../../../pages/call/types';
import { IGNORE_DEVICE_IDS } from '../../helpers/constants';
import CookieManager from '../../helpers/cookie-manager';
import { getLastConferenceConfig, isConferenceConfig } from '../../helpers/last-conference-config';
import { createLogger } from '../../helpers/loggerFacade';
import { isDeviceChangeAvailable, isDeviceListAvailable } from '../../helpers/media-devices';
import { countMetrika } from '../../helpers/metrika';
import isOnpremise from '../../helpers/onpremise/is-onpremise';
import Storage from '../../helpers/storage';
import WebStorage from '../../helpers/storage';
import { cloudApi } from '../../helpers/transport';
import { setBroadcastCaptionTooltipShown, setBroadcastTutorialStep } from '../config';
import { setCookieByConfig } from '../config/sagas';
import {
    getConfig,
    getIsMediaDevicesSyncEnabled,
    getTelemostAgent,
    isMessengerIntegration,
    isProductionEnvironmentSelector,
    isRoomAgentMode,
    selectConfigDevices,
    selectIsOnPremise,
    selectQuerySettings,
} from '../config/selectors';
import { rootDeviceManager, waitForDeviceManagerInit } from '../device-manager/sagas';
import { deviceManager, goloomSdk } from '../goloom-sdk';
import { NOTIFICATIONS_MAP, notify } from '../notifications/ps-notifications';
import { isMobileUa } from '../uatraits/selectors';
import { getUserSelector, setUser } from '../user';
import { selectUserUid } from '../user/selectors';
import {
    LAST_CONFERENCE_CONFIG_KEY,
    NOISE_CANCELLATION_TYPE,
    NOISE_CANCELLATION_TYPE_YANDEX,
    SESSION_STORAGE_DESCRIPTION_KEY,
    SESSION_STORAGE_EMAIL_KEY,
    VIRTUAL_BACKGROUND_TYPE_KEY,
} from './constants';
import { getUserSettings, setUserSettings } from './datasync';
import { SettingsTimeoutError } from './errors';
import {
    BroadcastTutorialStep,
    checkDeviceAvailable,
    getAudioOutputDeviceSelector,
    getDevices,
    getDeviceSelector,
    getUserInfoForSupportFeedback,
    getVideoProcessorOptions,
    hideSummarizationStartPopupAction,
    hideSummarizationWelcomePopupAction,
    initalVirtualBackgroundType,
    initialCheckDesktopAppExistence,
    initialIsShownLimitMessage,
    initialJoinWithCameraOff,
    initialJoinWithMicrophoneOff,
    initialNoiseCancellationType,
    initialShowMyVideoSetting,
    initialSummarizationStartPopupWasShown,
    initialSummarizationWelcomePopupWasShown,
    recordLimitMessageShown,
    refreshDevicesListAction,
    saveBroadcastCaptionTooltipShownAction,
    saveBroadcastTutorialShownAction,
    SecuritySettings,
    selectIsNoiseCancellationEnabled,
    selectIsSettingsReady,
    selectIsVirtualBackgroundEnabled,
    selectJoinWithCameraOff,
    selectJoinWithMicrophoneOff,
    selectSummarizationStartPopupWasShown,
    selectSummarizationWelcomePopupWasShown,
    selectVirtualBackgroundType,
    sendSupportFeedbackAction,
    setActiveAudioInputDeviceAction,
    setActiveAudioOutputDeviceAction,
    setActiveVideoInputDeviceAction,
    setAudioInputDeviceAction,
    setAudioOutputDeviceAction,
    setBandwidthEffective4kCodec,
    SetDeviceActionPayload,
    setDevicesAction,
    setDevicesChangeAvailableAction,
    setFeedbackFormVisibility,
    setHideParticipantsVideoAction,
    setIsSettingsReady,
    setIsShownLimitMessageAction,
    setJoinWithCameraOff,
    setJoinWithMicrophoneOff,
    setNoiseCancellationTypeAction,
    setSecuritySettingsAction,
    setStoreAudioInputDeviceIdAction,
    setStoreAudioOutputDeviceIdAction,
    setStoreCheckDesktopAppExistenceSettingsAction,
    setStoreNoiseCancellationTypeAction,
    setStoreSecuritySettingsAction,
    setStoreShowMyVideoSettingsAction,
    setStoreVideoInputDeviceIdAction,
    setSummarizationStartPopupWasShown,
    setSummarizationWelcomePopupWasShown,
    setVideoFacingModeAction,
    setVideoInputDeviceAction,
    setVirtualBackgroundType,
    setWasParticipantsVideoNotificationShownAction,
    StoreDevice,
    toggleBandwidthEffective4kCodecAction,
    toggleCameraFacingModeAction,
    toggleCheckDesktopAppExistenceSettings,
    toggleHideParticipantsVideoAction,
    toggleJoinWithCameraOffAction,
    toggleJoinWithMicrophoneOffAction,
    toggleShowMyVideoSettings,
} from './index';
import { canUseDatasyncPersistence, loadLocalUserSettings, persistLocalUserSetting } from './persistence';
import { FacingMode, SetDeviceActionMeta, SupportFeedbackForm } from './types';

const logger = createLogger('settings-sagas');

const LOAD_USER_SETTINGS_TIMEOUT = 3000;

/**
 * Утилитарная сага для ожидания загрузки настроек из localStorage.
 *
 * @param method - Название метода/компонента, который ждёт загрузки (для логирования)
 * @param customLogger - Опциональный логгер (по умолчанию используется logger из settings-sagas)
 * @param timeout - Опциональный таймаут в миллисекундах (по умолчанию 3000)
 */
export function* waitForSettingsLoaded(method: string, customLogger = logger, timeout = LOAD_USER_SETTINGS_TIMEOUT) {
    if (!(yield* select(selectIsSettingsReady))) {
        const { timeout: isTimeout } = yield* race({
            action: take(setIsSettingsReady),
            timeout: delay(timeout),
        });

        if (isTimeout) {
            const options = {
                method,
                additional: {
                    timeout,
                },
            };

            customLogger.warn(new SettingsTimeoutError(options));
        }
    }
}

/**
 * Валидирует deviceId и возвращает default в случае невалидности
 */
function ensureDeviceId(deviceId: string | null) {
    return deviceId === 'null' || !deviceId ? 'default' : deviceId;
}

function isBooleanValue(value: unknown): value is boolean {
    return typeof value === 'boolean';
}

function resolveStoredBooleanValue(storedValue: boolean | undefined, currentValue: boolean, condition = true): boolean {
    return condition && isBooleanValue(storedValue) ? storedValue : currentValue;
}

function* getSettingsPersistenceContext() {
    const userUid = yield* select(selectUserUid);
    const isOnPremise = yield* select(selectIsOnPremise);

    return {
        userUid,
        isOnPremise,
        shouldUseDatasync: canUseDatasyncPersistence({ isOnPremise, userUid }),
    };
}

export function getDeviceLabelOrDefault(device: StoreDevice) {
    return device.deviceId === 'default' ? 'default' : device.label;
}

export function findDeviceByLabel(devices: StoreDevice[], deviceLabel: Maybe<string>, deviceKind: MediaDeviceKind) {
    return devices.find((device) => {
        return (
            deviceKind === device.kind &&
            (getDeviceLabelOrDefault(device) === deviceLabel || device.label === deviceLabel)
        );
    });
}

/**
 * Задает аудио-устройство вывода
 *
 * @param {PayloadAction<SetDeviceActionPayload>} action
 */
function* setAudioOutputDeviceSaga({
    payload: { deviceId },
}: PayloadAction<SetDeviceActionPayload, string, SetDeviceActionMeta>) {
    try {
        const isMediaDevicesSyncEnabled = yield* select(getIsMediaDevicesSyncEnabled);

        if (!isMediaDevicesSyncEnabled) {
            yield* call([Storage, 'setItem'], 'audioOutputDeviceId', deviceId);

            const isOnPremise = yield* select(selectIsOnPremise);
            const device = yield* select((state) => getDeviceSelector(state, deviceId, 'audiooutput'));
            if (!isOnPremise && device) {
                yield* call([CookieManager, setCookieByConfig], 'telemostAudioOutputDeviceLabel', device.label);
            }
        }

        yield* put(setStoreAudioOutputDeviceIdAction(deviceId));
    } catch (error) {
        // eslint-disable-next-line no-console
        console.warn('Ошибка загрузки устройства аудио вывода:', error);
    }
}

/**
 * Задает аудио-устройство ввода
 *
 * @param {PayloadAction<SetDeviceActionPayload, string, SetDeviceActionMeta>} action
 */
function* setAudioInputDeviceSaga({
    payload: { deviceId },
}: PayloadAction<SetDeviceActionPayload, string, SetDeviceActionMeta>) {
    const isMediaDevicesSyncEnabled = yield* select(getIsMediaDevicesSyncEnabled);

    if (!isMediaDevicesSyncEnabled) {
        yield* call([Storage, Storage.setItem], 'audioInputDeviceId', deviceId);

        const isOnPremise = yield* select(selectIsOnPremise);
        const device = yield* select((state) => getDeviceSelector(state, deviceId, 'audioinput'));
        if (!isOnPremise && device) {
            yield* call([CookieManager, setCookieByConfig], 'telemostAudioInputDeviceLabel', device.label);
        }
    }

    yield* put(setStoreAudioInputDeviceIdAction(deviceId));
}

/**
 * Задает видео-устройство ввода
 *
 * @param {PayloadAction<SetDeviceActionPayload>} action
 */
function* setVideoInputDeviceSaga({
    payload: { deviceId },
}: PayloadAction<SetDeviceActionPayload, string, SetDeviceActionMeta>) {
    const isMediaDevicesSyncEnabled = yield* select(getIsMediaDevicesSyncEnabled);

    if (!isMediaDevicesSyncEnabled) {
        yield* call([Storage, Storage.setItem], 'videoInputDeviceId', deviceId);

        const isOnPremise = yield* select(selectIsOnPremise);
        const device = yield* select((state) => getDeviceSelector(state, deviceId, 'videoinput'));
        if (!isOnPremise && device) {
            yield* call([CookieManager, setCookieByConfig], 'telemostVideoInputDeviceLabel', device.label);
        }
    }

    yield* put(setStoreVideoInputDeviceIdAction(deviceId));
}

function* setVideoFacingModeSaga({ payload: facingMode }: PayloadAction<FacingMode>) {
    if (!goloomSdk.session) {
        yield* put(setVideoFacingModeAction(facingMode));
        yield* call([Storage, Storage.setItem], 'videoInputFacingMode', facingMode);

        const isOnPremise = yield* select(selectIsOnPremise);
        if (!isOnPremise) {
            yield* call([CookieManager, setCookieByConfig], 'telemostVideoInputFacingMode', facingMode);
        }
    }
}

function* setActiveAudioOutputDeviceSaga({ payload: deviceId }: PayloadAction<string>) {
    yield* put(setStoreAudioOutputDeviceIdAction(deviceId));
}

function* setActiveAudioInputDeviceSaga({ payload: deviceId }: PayloadAction<string>) {
    yield* put(setStoreAudioInputDeviceIdAction(deviceId));
}

function* setActiveVideoInputDeviceSaga({ payload: deviceId }: PayloadAction<string>) {
    yield* put(setStoreVideoInputDeviceIdAction(deviceId));
}

/**
 * Обновление списка девайсов
 */
export function* refreshMediaDevices(): Generator<Effect, void> {
    try {
        const deviceChangeAvailable = yield* all({
            input: call(isDeviceChangeAvailable, 'input'),
            output: call(isDeviceChangeAvailable, 'output'),
        });

        yield* put(setDevicesChangeAvailableAction(deviceChangeAvailable));

        if (isDeviceListAvailable()) {
            const devices = yield* call([deviceManager, deviceManager.requestDevices]);
            const audioDevices: Device[] = devices.audioInput.filter(({ id }) => !IGNORE_DEVICE_IDS.has(id));
            const videoDevices: Device[] = devices.videoInput.filter(({ id }) => !IGNORE_DEVICE_IDS.has(id));
            const audioOutputDevices: Device[] = devices.audioOutput.filter(({ id }) => !IGNORE_DEVICE_IDS.has(id));
            const storeDevices: StoreDevice[] = [
                ...audioDevices.map((device) => ({
                    deviceId: device.id,
                    kind: device.kind,
                    label: device.label,
                    groupId: device.group,
                })),
                ...videoDevices.map((device) => ({
                    deviceId: device.id,
                    kind: device.kind,
                    label: device.label,
                    groupId: device.group,
                })),
                ...audioOutputDevices.map((device) => ({
                    deviceId: device.id,
                    kind: device.kind,
                    label: device.label,
                    groupId: device.group,
                })),
            ];

            // Обновляем список девайсов в store
            yield* put(setDevicesAction(storeDevices));
            // Если текущее устройство вывода звука доступно, то выходим,
            // иначе обновляем id в store на 'default'
            const currentAudioOutputDevice = yield* select(getAudioOutputDeviceSelector);

            if (currentAudioOutputDevice && checkDeviceAvailable(currentAudioOutputDevice, storeDevices)) {
                return;
            }

            yield* put(setActiveAudioOutputDeviceAction('default'));
        }
    } catch (error) {
        // eslint-disable-next-line no-console
        console.error('refreshMediaDevices error', error);
    }
}

const createDeviceChangedChannel = () =>
    eventChannel((emitter) => {
        deviceManager.onDeviceChanged.addListener(emitter);
        return () => {};
    });

/**
 * Данная сага следит за списком доступных девайсов (аудио и видео) пользователя. При необходимости их обновляет
 */
function* watchMediaDevicesSaga(): Generator<Effect, void> {
    if (yield* select(isRoomAgentMode)) {
        return;
    }

    try {
        // Создадим канал, который подписывается на изменение списка девайсов до подключения к конференции
        const deviceChangedChannel = createDeviceChangedChannel();

        while (true) {
            // Когда список девайсов меняется, то обновляем список всех девайсов (в т.ч. активных)
            // или вылетает экшн обновления устройств
            yield* race([take(deviceChangedChannel), take(refreshDevicesListAction.type)]);
            yield* call(refreshMediaDevices);
        }
    } catch (error) {
        // eslint-disable-next-line no-console
        console.error('watchMediaDevicesSaga error', error);
    }
}

/**
 * Загрузка настроек безопасности из хранилища
 */
function* loadSecuritySettingsSaga() {
    try {
        const strSettings = yield* call([Storage, 'getItem'], 'security');

        if (strSettings) {
            const settings = JSON.parse(strSettings);
            yield* put(setStoreSecuritySettingsAction(settings));
        }
    } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Load settings error', error);
    }
}

/**
 * Сохранить настройки безопасности в хранилище
 */
function* saveSecuritySettingsSaga({ payload: newSecuritySettings }: PayloadAction<Partial<SecuritySettings>>) {
    try {
        yield* put(setStoreSecuritySettingsAction(newSecuritySettings));
        const securitySettings = yield* select((state: RootState) => state.settings.security);
        yield* call([Storage, 'setItem'], 'security', JSON.stringify(securitySettings));
    } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Save settings error', error);
    }
}

/**
 *
 * @param {GRID_VIEW} gridView
 * @returns {boolean}
 */
function isGridViewValid(gridView: GRID_VIEW): boolean {
    return Object.values(GRID_VIEW).includes(gridView);
}

type GridItemRatioType = 'default' | 'square';

export const itemRatioHash: Record<GridItemRatioType, GRID_ITEM_RATIO> = {
    default: GRID_ITEM_RATIO.DEFAULT,
    square: GRID_ITEM_RATIO.SQUARE,
};

/**
 *
 * @param {string | null} gridItemRatioType
 * @returns {GRID_ITEM_RATIO | null}
 */
function mapGridItemRatio(gridItemRatioType: string | null) {
    if (!gridItemRatioType) {
        return null;
    }

    return itemRatioHash[gridItemRatioType as GridItemRatioType] || null;
}

/**
 * Загрузка настроек пользователя из `datasync`-а
 * Для авторизованного загружаем настройки из DataSync
 * Для неавторизованного - из локального хранилища
 *
 * Также эта сага вызывается при смене пользователя, поэтому при отсутствии настройки нужно сбросить значение на дефолт
 */
export function* loadUserSettingsSaga(usePreloadConfigUserSettings = false) {
    try {
        const isMessenger = yield* select(isMessengerIntegration);

        let showMyVideo = initialShowMyVideoSetting;
        let showMyVideoFromStorage: boolean | undefined;

        let joinWithCameraOff = initialJoinWithCameraOff;
        let joinWithCameraOffFromStorage: boolean | undefined;

        let joinWithMicrophoneOff = initialJoinWithMicrophoneOff;
        let joinWithMicrophoneOffFromStorage: boolean | undefined;

        let checkDesktopAppExistence = initialCheckDesktopAppExistence;
        let checkDesktopAppExistenceFromStorage: boolean;

        let gridView = GRID_VIEW.DEFAULT;
        let gridViewFromStorage: GRID_VIEW | null | undefined;

        let isShownLimitMessage = initialIsShownLimitMessage;
        let isShownLimitMessageFromStorage: boolean;

        let summarizationWelcomePopupWasShown = initialSummarizationWelcomePopupWasShown;
        let summarizationWelcomePopupWasShownFromStorage: boolean | undefined;

        let summarizationStartPopupWasShown = initialSummarizationStartPopupWasShown;
        let summarizationStartPopupWasShownFromStorage: boolean | undefined;
        let broadcastTutorialStepFromStorage: BroadcastTutorialStep | undefined;
        let broadcastCaptionTooltipShownFromStorage: boolean | undefined;

        const {
            broadcastCaptionTooltipShown: configBroadcastCaptionTooltipShown,
            broadcastTutorialStep: configBroadcastTutorialStep,
            userSettings: configUserSettings,
            querySettings: configQuerySettings,
        } = yield* select(getConfig);

        const user = yield* select(getUserSelector);
        const { isOnPremise, shouldUseDatasync, userUid } = yield* call(getSettingsPersistenceContext);
        const settings: Partial<UserSettings> | undefined =
            usePreloadConfigUserSettings && configUserSettings && shouldUseDatasync
                ? configUserSettings
                : yield* call(function* () {
                      if (shouldUseDatasync && userUid) {
                          return yield* call(getUserSettings, userUid);
                      }
                  });

        if (user && settings) {
            broadcastTutorialStepFromStorage = settings.broadcastTutorialStep;
            broadcastCaptionTooltipShownFromStorage = settings.broadcastCaptionTooltipShown;
            showMyVideoFromStorage = settings.showMyVideo;
            // @ts-expect-error
            gridViewFromStorage = settings.gridView2;

            // @ts-expect-error
            checkDesktopAppExistenceFromStorage = settings.checkDesktopAppExistence;

            // @ts-expect-error
            isShownLimitMessageFromStorage = settings.isShownLimitMessage;

            joinWithCameraOffFromStorage = settings.joinWithCameraOff;
            joinWithMicrophoneOffFromStorage = settings.joinWithMicrophoneOff;

            summarizationWelcomePopupWasShownFromStorage = settings.summarizationWelcomePopupWasShown;
            summarizationStartPopupWasShownFromStorage = settings.summarizationStartPopupWasShown;
        } else {
            const localSettings = yield* call(loadLocalUserSettings, { isOnPremise });

            broadcastTutorialStepFromStorage = localSettings.broadcastTutorialStep;
            broadcastCaptionTooltipShownFromStorage = localSettings.broadcastCaptionTooltipShown;
            showMyVideoFromStorage = localSettings.showMyVideo;
            joinWithCameraOffFromStorage = localSettings.joinWithCameraOff;
            joinWithMicrophoneOffFromStorage = localSettings.joinWithMicrophoneOff;
            summarizationWelcomePopupWasShownFromStorage = localSettings.summarizationWelcomePopupWasShown;
            summarizationStartPopupWasShownFromStorage = localSettings.summarizationStartPopupWasShown;
            gridViewFromStorage = (localSettings.gridView2 as GRID_VIEW | undefined) ?? null;
            checkDesktopAppExistenceFromStorage = localSettings.checkDesktopAppExistence ?? true;
            isShownLimitMessageFromStorage = localSettings.isShownLimitMessage ?? false;
        }
        const isNotProduction = yield* select(isNonProductionEnvironment);
        const isDisableDatasync = new URLSearchParams(document.location.search).get('disable_datasync');

        if (isNotProduction && isDisableDatasync) {
            isShownLimitMessageFromStorage = false;
        }
        if (!isUndefined(configQuerySettings?.showMyVideo)) {
            showMyVideo = configQuerySettings?.showMyVideo;
        } else {
            showMyVideo = resolveStoredBooleanValue(showMyVideoFromStorage, showMyVideo);
        }

        joinWithCameraOff = resolveStoredBooleanValue(joinWithCameraOffFromStorage, joinWithCameraOff, !isMessenger);
        joinWithMicrophoneOff = resolveStoredBooleanValue(
            joinWithMicrophoneOffFromStorage,
            joinWithMicrophoneOff,
            !isMessenger
        );
        summarizationWelcomePopupWasShown = resolveStoredBooleanValue(
            summarizationWelcomePopupWasShownFromStorage,
            summarizationWelcomePopupWasShown
        );
        summarizationStartPopupWasShown = resolveStoredBooleanValue(
            summarizationStartPopupWasShownFromStorage,
            summarizationStartPopupWasShown
        );

        if (broadcastTutorialStepFromStorage && broadcastTutorialStepFromStorage !== configBroadcastTutorialStep) {
            yield* put(setBroadcastTutorialStep(broadcastTutorialStepFromStorage));
        }

        if (
            isBooleanValue(broadcastCaptionTooltipShownFromStorage) &&
            broadcastCaptionTooltipShownFromStorage !== configBroadcastCaptionTooltipShown
        ) {
            yield* put(setBroadcastCaptionTooltipShown(broadcastCaptionTooltipShownFromStorage));
        }

        const queryString = window.location.search;
        const searchParams = new URLSearchParams(queryString);
        const gridViewFromUrl = searchParams.get('grid_view') as GRID_VIEW | null;
        const gridItemRatioFromUrl = searchParams.get('grid_item_ratio') as string | null;
        const gridItemRatio = mapGridItemRatio(gridItemRatioFromUrl);

        // Приоритетно устанавливаем gridView по тому, что в урле
        if (gridViewFromUrl && isGridViewValid(gridViewFromUrl)) {
            gridView = gridViewFromUrl;
        } else if (gridViewFromStorage && isGridViewValid(gridViewFromStorage)) {
            gridView = gridViewFromStorage;
        }
        checkDesktopAppExistence = resolveStoredBooleanValue(
            checkDesktopAppExistenceFromStorage,
            checkDesktopAppExistence
        );

        const currentShowMyVideo = yield* select((state: RootState) => state.settings.media.showMyVideo);
        const currentJoinWithCameraOff = yield* select(selectJoinWithCameraOff);
        const currentJoinWithMicrophoneOff = yield* select(selectJoinWithMicrophoneOff);
        const currentSummarizationWelcomePopupWasShown = yield* select(selectSummarizationWelcomePopupWasShown);
        const currentSummarizationStartPopupWasShown = yield* select(selectSummarizationStartPopupWasShown);

        if (showMyVideo !== currentShowMyVideo) {
            yield* put(setStoreShowMyVideoSettingsAction(showMyVideo));
        }

        if (joinWithCameraOff !== currentJoinWithCameraOff) {
            yield* put(setJoinWithCameraOff(joinWithCameraOff));
        }

        if (joinWithMicrophoneOff !== currentJoinWithMicrophoneOff) {
            yield* put(setJoinWithMicrophoneOff(joinWithMicrophoneOff));
        }

        if (summarizationWelcomePopupWasShown !== currentSummarizationWelcomePopupWasShown) {
            yield* put(setSummarizationWelcomePopupWasShown(summarizationWelcomePopupWasShown));
        }

        if (summarizationStartPopupWasShown !== currentSummarizationStartPopupWasShown) {
            yield* put(setSummarizationStartPopupWasShown(summarizationStartPopupWasShown));
        }

        if (yield* select(selectIsNoiseCancellationEnabled)) {
            const isYandexUser = user?.isYandexSsoUser || user?.isYandexTeamUser;
            const noiseCancellationSettingKey = isYandexUser ? NOISE_CANCELLATION_TYPE_YANDEX : NOISE_CANCELLATION_TYPE;
            const noiseCancellationInitialValue: NoiseCancellationType = isYandexUser
                ? NoiseCancellationType.HIGH
                : initialNoiseCancellationType;
            let noiseCancellationType: NoiseCancellationType;
            if (isUndefined(configQuerySettings?.deviceProcessors.noiseCancellationType)) {
                const noiseCancelSettings = yield* call([Storage, 'getItem'], noiseCancellationSettingKey);
                const noiseCancellationTypeStorage = noiseCancelSettings && JSON.parse(noiseCancelSettings);
                noiseCancellationType = noiseCancellationTypeStorage ?? noiseCancellationInitialValue;
            } else {
                noiseCancellationType = configQuerySettings.deviceProcessors.noiseCancellationType;
            }

            const currentNoiseCancellationType = yield* select(
                (state: RootState) => state.settings.media.noiseCancellationType
            );
            if (noiseCancellationType !== currentNoiseCancellationType) {
                yield* put(setStoreNoiseCancellationTypeAction(noiseCancellationType));
            }
        }

        if (yield* select(selectIsVirtualBackgroundEnabled)) {
            let virtualBackgroundType: VirtualBackgroundType;
            if (isUndefined(configQuerySettings?.deviceProcessors.virtualBackground)) {
                const virtualBackgroundTypeStorage = (yield* call(
                    [Storage, Storage.getItem],
                    VIRTUAL_BACKGROUND_TYPE_KEY
                )) as VirtualBackgroundType | null;
                virtualBackgroundType = virtualBackgroundTypeStorage ?? initalVirtualBackgroundType;
            } else {
                virtualBackgroundType = configQuerySettings.deviceProcessors.virtualBackground;
            }
            const currentVirtualBackgroundType = yield* select(selectVirtualBackgroundType);
            if (virtualBackgroundType !== currentVirtualBackgroundType) {
                yield* put(setVirtualBackgroundType(virtualBackgroundType));
            }
        }

        if (!isMessenger) {
            let hideParticipantsVideo;
            if (!isUndefined(configQuerySettings?.hideParticipantsVideo)) {
                hideParticipantsVideo = configQuerySettings.hideParticipantsVideo;
            } else {
                hideParticipantsVideo = yield* call([Storage, 'getParsedItem'], 'hideParticipantsVideo');
            }
            yield* put(setHideParticipantsVideoAction(hideParticipantsVideo));
            const wasParticipantsVideoNotificationShown = yield* call(
                [Storage, 'getParsedItem'],
                'wasParticipantsVideoNotificationShown'
            );
            yield* put(setWasParticipantsVideoNotificationShownAction(wasParticipantsVideoNotificationShown));
        }

        const bandwidthEffective4kCodec = yield* call([Storage, 'getParsedItem'], 'bandwidthEffective4kCodec');
        if (typeof bandwidthEffective4kCodec === 'boolean') {
            yield* put(setBandwidthEffective4kCodec(bandwidthEffective4kCodec));
        }

        const currentCheckDesktopAppExistence = yield* select(
            (state: RootState) => state.settings.checkDesktopAppExistence
        );
        if (checkDesktopAppExistence !== currentCheckDesktopAppExistence) {
            yield* put(setStoreCheckDesktopAppExistenceSettingsAction(checkDesktopAppExistence));
        }

        isShownLimitMessage = resolveStoredBooleanValue(isShownLimitMessageFromStorage, isShownLimitMessage);

        const currentIsShownLimitMessage = yield* select((state: RootState) => state.settings.isShownLimitMessage);

        if (isShownLimitMessage !== currentIsShownLimitMessage) {
            yield* put(setIsShownLimitMessageAction(isShownLimitMessage));
        }
        const isNewGrid = yield* select(selectIsNewGrid);
        const isMobile = yield* select(isMobileUa);
        yield* put(setStoreUserPreferredGridView({ gridView }));
        yield* put(setStoreActiveGridView({ gridView, isMobile, isNewGrid }));

        if (gridItemRatio) {
            yield* put(setStoreGridItemRatio({ gridItemRatio }));
        }

        yield* put(setIsSettingsReady());

        countMetrika('settings', 'see_myself_on_screen_state', showMyVideo.toString());
    } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Load user settings error', error);
    }
}

/**
 * Загрузка ранее выбранных устройств из localStorage
 * Если устройство уже отключено то не записываем его в стор
 */
function* loadDeviceSettingsSaga() {
    const isMediaDevicesSyncEnabled = yield* select(getIsMediaDevicesSyncEnabled);
    const deviceLabels = yield* select(selectConfigDevices);
    const hasAnyDeviceLabel = Object.values(deviceLabels ?? {}).filter(Boolean).length > 0;
    yield* call(waitForDeviceManagerInit);
    yield* call(refreshMediaDevices);
    const devices = yield* select(getDevices);

    let videoInputDeviceId = null;
    let audioInputDeviceId = null;
    let audioOutputDeviceId = null;

    /**
     * Телемост открыт в айфрейме и хост передаст свой список устройств через событие
     */
    if (isMediaDevicesSyncEnabled && !hasAnyDeviceLabel) {
        return;
    }

    if (hasAnyDeviceLabel) {
        const videoInputDevice = yield* call(findDeviceByLabel, devices, deviceLabels.videoInputLabel, 'videoinput');
        const audioInputDevice = yield* call(findDeviceByLabel, devices, deviceLabels.audioInputLabel, 'audioinput');
        const audioOutputDevice = yield* call(findDeviceByLabel, devices, deviceLabels.audioOutputLabel, 'audiooutput');

        videoInputDeviceId = videoInputDevice?.deviceId ?? null;
        audioInputDeviceId = audioInputDevice?.deviceId ?? null;
        audioOutputDeviceId = audioOutputDevice?.deviceId ?? null;
    }

    const isOnPremise = yield* select(selectIsOnPremise);
    if (!isMediaDevicesSyncEnabled && !hasAnyDeviceLabel) {
        const videoInputDeviceLabelFromCookie = !isOnPremise
            ? yield* call([CookieManager, CookieManager.get], 'telemostVideoInputDeviceLabel')
            : null;
        const audioInputDeviceLabelFromCookie = !isOnpremise
            ? yield* call([CookieManager, CookieManager.get], 'telemostAudioInputDeviceLabel')
            : null;
        const audioOutputDeviceLabelFromCookie = !isOnpremise
            ? yield* call([CookieManager, CookieManager.get], 'telemostAudioOutputDeviceLabel')
            : null;

        const videoInputDeviceByCookie = videoInputDeviceLabelFromCookie
            ? yield* call(findDeviceByLabel, devices, videoInputDeviceLabelFromCookie, 'videoinput')
            : null;
        const audioInputDeviceByCookie = audioInputDeviceLabelFromCookie
            ? yield* call(findDeviceByLabel, devices, audioInputDeviceLabelFromCookie, 'audioinput')
            : null;
        const audioOutputDeviceByCookie = audioOutputDeviceLabelFromCookie
            ? yield* call(findDeviceByLabel, devices, audioOutputDeviceLabelFromCookie, 'audiooutput')
            : null;

        videoInputDeviceId =
            videoInputDeviceByCookie?.deviceId ?? (yield* call([Storage, Storage.getItem], 'videoInputDeviceId'));
        audioInputDeviceId =
            audioInputDeviceByCookie?.deviceId ?? (yield* call([Storage, Storage.getItem], 'audioInputDeviceId'));
        audioOutputDeviceId =
            audioOutputDeviceByCookie?.deviceId ?? (yield* call([Storage, Storage.getItem], 'audioOutputDeviceId'));
    }
    const videoInputFacingModeFromCookie = !isOnPremise
        ? yield* call([CookieManager, CookieManager.get], 'telemostVideoInputFacingMode')
        : null;
    const videoInputFacingMode =
        videoInputFacingModeFromCookie ?? (yield* call([Storage, Storage.getItem], 'videoInputFacingMode'));

    /**
     * Вызываем саги напрямую, т.к. хотим только сохранить значение в store, но не триггерить другие саги.
     * Если сделать put setVideoInputDeviceAction (или другого action), то произойдет захват видео/аудио до старта
     * конференции, например на главном экране.
     */
    yield* call(setVideoInputDeviceSaga, {
        payload: { deviceId: ensureDeviceId(videoInputDeviceId) },
        meta: { isInit: true },
    } as PayloadAction<SetDeviceActionPayload, string, SetDeviceActionMeta>);
    yield* call(setAudioInputDeviceSaga, {
        payload: { deviceId: ensureDeviceId(audioInputDeviceId) },
        meta: { isInit: true },
    } as PayloadAction<SetDeviceActionPayload, string, SetDeviceActionMeta>);
    yield* call(setAudioOutputDeviceSaga, {
        payload: { deviceId: ensureDeviceId(audioOutputDeviceId) },
        meta: { isInit: true },
    } as PayloadAction<SetDeviceActionPayload, string, SetDeviceActionMeta>);

    if ([FacingMode.USER, FacingMode.ENVIRONMENT].includes(videoInputFacingMode as FacingMode)) {
        yield* call(setVideoFacingModeSaga, toggleCameraFacingModeAction(videoInputFacingMode as FacingMode));
    }
}

/**
 * Переключает и сохраняет в хранилище настройку "Показывать моё видео"
 * Для авторизованного сохраняет настройку в DataSync
 * Для неавторизованного - в локальном хранилище
 */
export function* toggleShowMyVideoSaga({ payload: value }: PayloadAction<boolean | undefined>) {
    try {
        const newShowMyVideo =
            typeof value === 'boolean' ? value : yield* select((state: RootState) => !state.settings.media.showMyVideo);

        yield* put(setStoreShowMyVideoSettingsAction(newShowMyVideo));

        const { shouldUseDatasync, userUid } = yield* call(getSettingsPersistenceContext);
        if (shouldUseDatasync && userUid) {
            yield* call(setUserSettings, userUid, 'showMyVideo', newShowMyVideo);
        } else {
            yield* call(persistLocalUserSetting, 'showMyVideo', newShowMyVideo);
        }
    } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Save settings error', error);
    }
}

/**
 * Переключает и сохраняет в хранилище настройку "Не проверять наличие ПО"
 */
function* toggleCheckDesktopAppExistenceSaga() {
    try {
        const newValue = yield* select((state: RootState) => !state.settings.checkDesktopAppExistence);
        yield* put(setStoreCheckDesktopAppExistenceSettingsAction(newValue));

        const { shouldUseDatasync, userUid } = yield* call(getSettingsPersistenceContext);
        if (shouldUseDatasync && userUid) {
            yield* call(setUserSettings, userUid, 'checkDesktopAppExistence', newValue);
        } else {
            yield* call(persistLocalUserSetting, 'checkDesktopAppExistence', newValue);
        }
    } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Save settings error', error);
    }
}

/**
 * Добавляет в хранилище настройку предпочитаемого вида сетки
 * Для авторизованного - сохраняет настройку в DataSync
 * Для неавторизованного - в локальном хранилище
 */
function* setGridViewSaga({
    payload: { gridView },
}: PayloadAction<{
    gridView: GRID_VIEW;
}>) {
    try {
        const { shouldUseDatasync, userUid } = yield* call(getSettingsPersistenceContext);

        if (shouldUseDatasync && userUid) {
            yield* call(setUserSettings, userUid, 'gridView2', gridView);
        } else {
            yield* call(persistLocalUserSetting, 'gridView2', gridView);
        }
    } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Save grid view setting error', error);
    }
}

/**
 * При смене пользователя необходимо подгрузить его настройки
 */
function* onChangeUserSaga() {
    const user = yield* select(getUserSelector);
    if (user) {
        yield* call(loadUserSettingsSaga);
    }
}

/**
 * Запоминаем, показывали ли мы пользователю сообщение об ограничении записи
 */
function* recordLimitMessageShownSaga() {
    const { shouldUseDatasync, userUid } = yield* call(getSettingsPersistenceContext);

    try {
        if (shouldUseDatasync && userUid) {
            yield* call(setUserSettings, userUid, 'isShownLimitMessage', true);
        } else {
            yield* call(persistLocalUserSetting, 'isShownLimitMessage', true);
        }
        yield* put(setIsShownLimitMessageAction(true));
    } catch (error) {
        logger.error(error, {
            method: 'setIsShownLimitMessage',
        });
    }
}

/**
 *
 */
function* saveBroadcastTutorialShownSaga({ payload }: PayloadAction<BroadcastTutorialStep>) {
    yield* put(setBroadcastTutorialStep(payload));

    try {
        const { isOnPremise, shouldUseDatasync, userUid } = yield* call(getSettingsPersistenceContext);

        if (shouldUseDatasync && userUid) {
            yield* call(setUserSettings, userUid, 'broadcastTutorialStep', payload);
        } else if (isOnPremise) {
            yield* call(persistLocalUserSetting, 'broadcastTutorialStep', payload);
        }
    } catch (error) {
        logger.error(error, {
            method: 'saveBroadcastTutorialShownSaga',
        });
    }
}

/**
 *
 */
function* saveBroadcastCaptionTooltipShownSaga({ payload }: PayloadAction<boolean>) {
    yield* put(setBroadcastCaptionTooltipShown(payload));

    try {
        const { isOnPremise, shouldUseDatasync, userUid } = yield* call(getSettingsPersistenceContext);

        if (shouldUseDatasync && userUid) {
            yield* call(setUserSettings, userUid, 'broadcastCaptionTooltipShown', payload);
        } else if (isOnPremise) {
            yield* call(persistLocalUserSetting, 'broadcastCaptionTooltipShown', payload);
        }
    } catch (error) {
        logger.error(error, {
            method: 'saveBroadcastCaptionTooltipShownSaga',
        });
    }
}

/**
 * Cохраняет в хранилище и store настройку "Шумоподавление"
 * @param {boolean} [isTemporarily] Флаг о том, что значение временное и сохранять его не нужно
 */
export function* setNoiseCancellationType(value: NoiseCancellationType, isTemporarily?: boolean) {
    try {
        yield* put(setStoreNoiseCancellationTypeAction(value));

        const user = yield* select(getUserSelector);
        const settingKey =
            user?.isYandexSsoUser || user?.isYandexTeamUser ? NOISE_CANCELLATION_TYPE_YANDEX : NOISE_CANCELLATION_TYPE;

        if (!isTemporarily) {
            yield* call([Storage, 'setItem'], settingKey, JSON.stringify(value));
        }
    } catch (error) {
        logger.error(error, {
            method: 'updateNoiseCancellationTypeSaga',
        });
    }
}

/**
 * Сохраняет и передает в goloomSdk настройку "Шумоподавление"
 */
function* setNoiseCancellationTypeSaga({ payload: value }: PayloadAction<NoiseCancellationType>) {
    logger.event(`Noise cancellation changed. Source: Settings. Value: ${value}`, {
        additional: {
            feature: 'noise_cancellation',
            source: 'Settings',
            value,
        },
    });
    try {
        yield* call(setNoiseCancellationType, value);
        yield* call([deviceManager, deviceManager.changeSoundProcessorOptions], value);
    } catch (error) {
        logger.error(error, {
            method: 'setNoiseCancellationTypeSaga',
        });
    }
}

/**
 * Сохраняет и передает в goloomSdk настройку videoProcessorOptions
 */
function* setVirtualBackgroundTypeSaga({ payload }: PayloadAction<VirtualBackgroundType>) {
    const isVirtualBackgroundEnabled = yield* select(selectIsVirtualBackgroundEnabled);
    const videoProcessorOptions = yield* call(getVideoProcessorOptions, payload, isVirtualBackgroundEnabled);
    yield* call([deviceManager, deviceManager.setVideoProcessorOptions], videoProcessorOptions);
    yield* call([Storage, Storage.setItem], VIRTUAL_BACKGROUND_TYPE_KEY, payload);
}

function* setConferenceConfigToLocalStorageSaga({ payload }: PayloadAction<Maybe<ConferenceConfig>>) {
    if (!payload) {
        return;
    }
    const payloadWithDate = Object.assign(
        {
            time: new Date().toISOString(),
        },
        payload
    );
    yield* call([WebStorage, WebStorage.setItem], LAST_CONFERENCE_CONFIG_KEY, JSON.stringify(payloadWithDate));
}

function* sendSupportFeedbackSaga({ payload }: PayloadAction<SupportFeedbackForm>) {
    const isProduction = yield* select(isProductionEnvironmentSelector);
    const userInfo = yield* select(getUserInfoForSupportFeedback);
    const telemostAgent = yield* select(getTelemostAgent);

    const requestBody: CloudAPI.SendFeedbackToSupport.Request = {
        fos_support_text: payload.description,
        debug_info: {
            ...userInfo,
            room_id: '',
            conference_uri: '',
            peer_id: '',
            media_platform: '',
            time: '',
            goloom_sdk_version: GOLOOM_SDK_VERSION,
            goloom_sdk_react_version: GOLOOM_SDK_REACT_VERSION,
            telemost_sdk_version: TELEMOST_SDK_VERSION,
        },
    };
    const conferenceConfig = yield* call(getLastConferenceConfig);
    if (isConferenceConfig(conferenceConfig)) {
        requestBody.debug_info.room_id = conferenceConfig?.room_id;
        requestBody.debug_info.conference_uri = conferenceConfig?.resolved_uri;
        requestBody.debug_info.peer_id = conferenceConfig?.peer_id;
        requestBody.debug_info.media_platform = conferenceConfig?.media_platform ?? '';
        requestBody.debug_info.time = conferenceConfig?.time ?? new Date().toISOString();
    }

    const query: CloudAPI.SendFeedbackToSupport.Query = {
        subject: telemostAgent,
        reply_email: payload.email,
        app_version: userInfo?.telemost_version,
        os_version: userInfo?.os_version?.toString?.(),
        recipient_type: isProduction ? 'supports' : 'testers',
    };

    try {
        yield* call([cloudApi, cloudApi.sendFeedbackToSupport], query, requestBody);
        yield* put(notify(NOTIFICATIONS_MAP[NotificationId.SUPPORT_SERVICE_APPLY]()));
        yield* put(setFeedbackFormVisibility(false));
        yield* call([sessionStorage, sessionStorage.removeItem], SESSION_STORAGE_EMAIL_KEY);
        yield* call([sessionStorage, sessionStorage.removeItem], SESSION_STORAGE_DESCRIPTION_KEY);
    } catch (error) {
        yield* put(notify(NOTIFICATIONS_MAP[NotificationId.SEND_FEEDBACK_ERROR]()));
        logger.error(error, {
            method: 'sendSupportFeedbackSaga',
        });
    }
}

function* toggleJoinWithCameraOffSaga({ payload }: PayloadAction<boolean>) {
    try {
        const { shouldUseDatasync, userUid } = yield* call(getSettingsPersistenceContext);

        if (shouldUseDatasync && userUid) {
            yield* call(setUserSettings, userUid, 'joinWithCameraOff', payload);
        } else {
            yield* call(persistLocalUserSetting, 'joinWithCameraOff', payload);
        }
        yield* put(setJoinWithCameraOff(payload));
    } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Save settings error', error);
    }
}

function* toggleJoinWithMicrophoneOffSaga({ payload }: PayloadAction<boolean>) {
    try {
        const { shouldUseDatasync, userUid } = yield* call(getSettingsPersistenceContext);

        if (shouldUseDatasync && userUid) {
            yield* call(setUserSettings, userUid, 'joinWithMicrophoneOff', payload);
        } else {
            yield* call(persistLocalUserSetting, 'joinWithMicrophoneOff', payload);
        }
        yield* put(setJoinWithMicrophoneOff(payload));
    } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Save settings error', error);
    }
}

/**
 * Cохраняет в хранилище настройку "Скрыть видео участников"
 */
function* toggleHideParticipantsVideoSaga({ payload }: PayloadAction<boolean>) {
    try {
        const isMessenger = yield* select(isMessengerIntegration);
        yield* put(setHideParticipantsVideoAction(payload));
        yield* put(setWasParticipantsVideoNotificationShownAction(false));
        if (!isMessenger) {
            yield* call([Storage, 'setItem'], 'hideParticipantsVideo', JSON.stringify(payload));
            yield* call([Storage, 'setItem'], 'wasParticipantsVideoNotificationShown', JSON.stringify(false));
        }
    } catch (error) {
        logger.error(error, {
            method: 'toggleHideParticipantsVideoSaga',
        });
    }
}

function* toggleBandwidthEffective4kCodecSaga({ payload }: PayloadAction<boolean>) {
    try {
        yield* put(setBandwidthEffective4kCodec(payload));
        yield* call([Storage, 'setItem'], 'bandwidthEffective4kCodec', JSON.stringify(payload));
    } catch (error) {
        logger.error(error, {
            method: 'toggleBandwidthEffective4kCodecSaga',
        });
    }
}

function* hideSummarizationWelcomePopupSaga() {
    yield* put(setSummarizationWelcomePopupWasShown(true));

    try {
        const { shouldUseDatasync, userUid } = yield* call(getSettingsPersistenceContext);

        if (shouldUseDatasync && userUid) {
            yield* call(setUserSettings, userUid, 'summarizationWelcomePopupWasShown', true);
        } else {
            yield* call(persistLocalUserSetting, 'summarizationWelcomePopupWasShown', true);
        }
    } catch (error) {
        logger.error(error, {
            method: 'hideSummarizationWelcomePopupSaga',
        });
    }
}

function* hideSummarizationStartPopupSaga() {
    yield* put(setSummarizationStartPopupWasShown(true));

    try {
        const { shouldUseDatasync, userUid } = yield* call(getSettingsPersistenceContext);

        if (shouldUseDatasync && userUid) {
            yield* call(setUserSettings, userUid, 'summarizationStartPopupWasShown', true);
        } else {
            yield* call(persistLocalUserSetting, 'summarizationStartPopupWasShown', true);
        }
    } catch (error) {
        logger.error(error, {
            method: 'hideSummarizationStartPopupSaga',
        });
    }
}

function* loadQuerySettingsSaga() {
    const querySettings = yield* select(selectQuerySettings);
    const isMessenger = yield* select(isMessengerIntegration);
    const showMyVideo = querySettings?.showMyVideo;
    const hideParticipantsVideo = querySettings?.hideParticipantsVideo;
    const noiseCancellationType = querySettings?.deviceProcessors.noiseCancellationType;
    const virtualBackgroundType = querySettings?.deviceProcessors.virtualBackground;
    if (!isUndefined(showMyVideo)) {
        yield* put(setStoreShowMyVideoSettingsAction(showMyVideo));
    }
    if (!isUndefined(hideParticipantsVideo) && !isMessenger) {
        yield* put(setHideParticipantsVideoAction(hideParticipantsVideo));
    }
    if (!isUndefined(noiseCancellationType) && (yield* select(selectIsNoiseCancellationEnabled))) {
        yield* put(setStoreNoiseCancellationTypeAction(noiseCancellationType));
    }
    if (!isUndefined(virtualBackgroundType) && (yield* select(selectIsVirtualBackgroundEnabled))) {
        yield* put(setVirtualBackgroundType(virtualBackgroundType));
    }
}

const rootSaga = function* () {
    if (NODE) {
        return;
    }

    yield* all([
        call(loadQuerySettingsSaga),
        fork(watchMediaDevicesSaga),
        fork(loadSecuritySettingsSaga),
        fork(loadUserSettingsSaga, true),
        fork(loadDeviceSettingsSaga),
        fork(rootDeviceManager),
        takeEvery(setAudioOutputDeviceAction, setAudioOutputDeviceSaga),
        takeEvery(setAudioInputDeviceAction, setAudioInputDeviceSaga),
        takeEvery(setVideoInputDeviceAction, setVideoInputDeviceSaga),
        takeEvery(toggleCameraFacingModeAction, setVideoFacingModeSaga),
        takeEvery(setActiveAudioOutputDeviceAction, setActiveAudioOutputDeviceSaga),
        takeEvery(setActiveAudioInputDeviceAction, setActiveAudioInputDeviceSaga),
        takeEvery(setActiveVideoInputDeviceAction, setActiveVideoInputDeviceSaga),
        takeEvery(setSecuritySettingsAction, saveSecuritySettingsSaga),
        takeEvery(toggleShowMyVideoSettings, toggleShowMyVideoSaga),
        takeEvery(toggleCheckDesktopAppExistenceSettings, toggleCheckDesktopAppExistenceSaga),
        takeEvery(setUser, onChangeUserSaga),
        takeEvery(setUserPreferredGridViewAction, setGridViewSaga),
        takeEvery(recordLimitMessageShown, recordLimitMessageShownSaga),
        takeEvery(saveBroadcastTutorialShownAction, saveBroadcastTutorialShownSaga),
        takeEvery(saveBroadcastCaptionTooltipShownAction, saveBroadcastCaptionTooltipShownSaga),
        takeEvery(setNoiseCancellationTypeAction, setNoiseCancellationTypeSaga),
        takeLatest(setVirtualBackgroundType, setVirtualBackgroundTypeSaga),
        takeLatest(setRoomConferenceConfig, setConferenceConfigToLocalStorageSaga),
        takeLatest(sendSupportFeedbackAction, sendSupportFeedbackSaga),
        takeLatest(toggleJoinWithCameraOffAction, toggleJoinWithCameraOffSaga),
        takeLatest(toggleJoinWithMicrophoneOffAction, toggleJoinWithMicrophoneOffSaga),
        takeLatest(toggleHideParticipantsVideoAction, toggleHideParticipantsVideoSaga),
        takeLatest(toggleBandwidthEffective4kCodecAction, toggleBandwidthEffective4kCodecSaga),
        takeLatest(hideSummarizationWelcomePopupAction, hideSummarizationWelcomePopupSaga),
        takeLatest(hideSummarizationStartPopupAction, hideSummarizationStartPopupSaga),
    ]);
};

export default rootSaga;
